import type { Command, EditorState } from 'prosemirror-state';
import type { IntlShape } from 'react-intl-next';

import type {
	ExtractInjectionAPI,
	FloatingToolbarConfig,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types';
import { isEmptyNode as isEmptyNodeWithSchema } from '@atlaskit/editor-common/utils';
import type { Node as PMNode, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { ASK_ROVO_MEDIA_NODE_TYPES, REMIX_DENY_LIST_V1 } from '../remix-config';

export function getUnallowedNodes(schema: Schema): Set<NodeType> {
	return new Set(REMIX_DENY_LIST_V1.map((name) => schema.nodes[name]).filter(Boolean));
}

export function getMediaNodesForAskRovo(schema: Schema): Set<NodeType> {
	return new Set(ASK_ROVO_MEDIA_NODE_TYPES.map((name) => schema.nodes[name]).filter(Boolean));
}

export function getOverriddenFloatingToolbarConfig(
	api?: ExtractInjectionAPI<MalleableUiPlugin>,
): (
	state: EditorState,
	intl: IntlShape,
	_providerFactory: unknown,
	activeConfigs?: Array<FloatingToolbarConfig>,
) => FloatingToolbarConfig | undefined {
	if (!api?.toolbar) {
		return () => undefined;
	}

	return (
		state: EditorState,
		intl: IntlShape,
		_providerFactory: unknown,
		activeConfigs?: Array<FloatingToolbarConfig>,
	): FloatingToolbarConfig | undefined => {
		if (!activeConfigs?.length) {
			return;
		}

		const unallowedNodes = getUnallowedNodes(state.schema);
		const isNodeEmpty = isEmptyNodeWithSchema(state.schema);
		activeConfigs.forEach((config) => {
			if (
				([] as NodeType[])
					.concat(config.nodeType)
					.some((nodeType) => unallowedNodes.has(nodeType) || nodeType.isInline)
			) {
				return;
			}

			const toolbarItems = config.items;
			if (Array.isArray(toolbarItems)) {
				const remixButton: FloatingToolbarItem<Command> = {
					id: 'editor.malleable-ui.remix',
					type: 'button',
					title: intl.formatMessage(messages.remixToolbarLabel),
					tooltipContent: intl.formatMessage(messages.remixToolbarTooltip),
					showTitle: true,
					hideTooltipOnClick: false,
					icon: AiGenerativeRemixIcon,
					onClick: () => false,
					disabled: false,
					testId: 'editor-floating-toolbar__remix-button',
				};
				const remixToolbarItems: FloatingToolbarItem<Command>[] = [
					remixButton,
					{
						type: 'separator',
						fullHeight: true,
					},
				];

				toolbarItems.splice(0, 0, ...remixToolbarItems);
				config.items = (node) => {
					remixButton.onClick = (editorState) => {
						const $pos = editorState.selection.$from;
						let nodePos: number | undefined;
						for (let depth = $pos.depth; depth > 0; depth--) {
							if ($pos.node(depth) === node) {
								nodePos = $pos.before(depth);
								break;
							}
						}
						api.core.actions.execute(api.malleableUi.commands.openRemixModal(nodePos));
						return true;
					};
					remixButton.disabled = isNodeEmpty(node);
					return toolbarItems;
				};
			}
		});

		const mediaNodesForAskRovo = getMediaNodesForAskRovo(state.schema);
		const askRovoButton = api?.aiExperience?.actions?.getAskRovoFloatingToolbarButton?.({
			intl,
			disabled: false,
		});
		if (!mediaNodesForAskRovo.size || !askRovoButton) {
			return;
		}

		const askRovoItems: FloatingToolbarItem<Command>[] = [
			askRovoButton,
			{
				type: 'separator',
				fullHeight: true,
			},
		];

		const shouldShowAskRovoForMediaNode = (node: PMNode) =>
			mediaNodesForAskRovo.has(node.type) && !node.type.isInline;

		activeConfigs.forEach((config) => {
			const nodeTypes = ([] as NodeType[]).concat(config.nodeType);
			const isMediaConfig = nodeTypes.some(
				(nodeType) => mediaNodesForAskRovo.has(nodeType) && !nodeType.isInline,
			);
			if (!isMediaConfig) {
				return;
			}

			const items = config.items;
			if (Array.isArray(items)) {
				config.items = (node) =>
					shouldShowAskRovoForMediaNode(node) ? [...askRovoItems, ...items] : items;
			} else if (typeof items === 'function') {
				config.items = (node) => {
					const baseItems = items(node);
					return shouldShowAskRovoForMediaNode(node) ? [...askRovoItems, ...baseItems] : baseItems;
				};
			}
		});
	};
}
